import { LayoutTemplate } from "../LayoutTemplate";
import { ChartsReferenceLine, LineChart } from "@mui/x-charts";
import dayjs from "dayjs";
import { ILineChartprops, useDataTendencia } from "./useData";
import { GraphColors } from "../../../utils/graphCorlors";
import { Capitalize } from "../../../utils/capitalize";
import {
  ContinuousCalendar,
  HEAT_COLORS,
  dayKey,
} from "../../Molecules/ContinuousCalendar/ContinuousCalendar";
import {
  ChartLegend,
  card,
  chartSx,
  num,
  pct,
  pctLabel,
} from "../GeneralTemplate/ui";

const kpiCard = `${card} flex flex-[1_1_200px] flex-col gap-3.5`;
const kpiLabel = "text-[13px] font-medium text-[#6B6B66]";
const kpiValue = "text-4xl font-bold tabular-nums tracking-tight";
const statBox = "flex flex-col gap-1.5 rounded-xl bg-[#F7F7F5] p-4";

const TrendChart = ({
  title,
  subtitle,
  chartData,
  reference,
}: {
  title: string;
  subtitle: string;
  chartData: ILineChartprops;
  reference?: number;
}) => {
  const series = chartData?.series || [];
  const legend = series.map((s) => ({ label: s.label as string, color: s.color }));
  // Inclui a necessidade no domínio para a linha de referência não sair do gráfico
  const yMax = reference
    ? Math.max(reference * 1.1, ...series.flatMap((s) => (s.data as number[]) || []))
    : undefined;

  return (
    <div className={`${card} flex min-w-0 flex-[1_1_520px] flex-col gap-4`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-semibold">{title}</h2>
          <span className="text-[13px] text-[#6B6B66]">{subtitle}</span>
        </div>
        <div className="flex flex-wrap gap-3.5">
          <ChartLegend series={legend} />
          {reference !== undefined && (
            <span className="inline-flex items-center gap-1.5 text-[13px] text-[#4A4A46]">
              <span className="w-3.5 border-t-2 border-dashed border-[#E84E1B]" />
              Necessidade/dia ({num(reference)})
            </span>
          )}
        </div>
      </div>
      <LineChart
        height={260}
        margin={{ top: 16, right: 16, bottom: 30, left: 44 }}
        xAxis={[{ scaleType: "band", data: chartData?.xAxis || [] }]}
        yAxis={[{ min: 0, max: yMax }]}
        series={series}
        slotProps={{ legend: { hidden: true } }}
        sx={chartSx}
      >
        {reference !== undefined && (
          <ChartsReferenceLine
            y={reference}
            lineStyle={{ stroke: "#E84E1B", strokeDasharray: "6 4" }}
          />
        )}
      </LineChart>
    </div>
  );
};

export const TendenciesTemplate = () => {
  const {
    data,
    tendenciasGeral,
    tendenciasTipoServico,
    tendenciasUnidade,
    tendenciasAtendimento,
  } = useDataTendencia();

  const meta = data?.meta || 0;
  const unidades = data?.unidades || [];
  const diaria = data?.producaoDiaria || [];

  // Mês de referência vem dos dados; fora do mês corrente, o mês já terminou
  const month = dayjs(dayKey(diaria[0]?.data) || undefined);
  const daysInMonth = month.daysInMonth();
  const elapsed = month.isSame(dayjs(), "month") ? dayjs().date() : daysInMonth;
  const restante = daysInMonth - elapsed;
  const pace = pct(elapsed, daysInMonth);

  const worked = diaria.filter((d) => d.qtdTotal > 0);
  const best = worked.reduce<(typeof diaria)[number] | undefined>(
    (acc, d) => (!acc || d.qtdTotal > acc.qtdTotal ? d : acc),
    undefined
  );
  const media = worked.length
    ? worked.reduce((acc, d) => acc + d.qtdTotal, 0) / worked.length
    : 0;

  const acima = (data?.tendencia || 0) >= 100;
  const sum = (key: "meta" | "qtdTotal" | "qtdMedia" | "qtdMediaNecessaria" | "qtdMediaProjecao") =>
    unidades.reduce((acc, u) => acc + (u[key] || 0), 0);

  return (
    <LayoutTemplate title="Dashboard de Tendência">
      <div className="mb-8 flex flex-col gap-5 text-[#1D1D1B]">
        <span className="inline-flex items-center gap-2 text-sm text-[#6B6B66]">
          <span
            className="h-2 w-2 rounded-full"
            style={{ background: GraphColors[Capitalize(data?.empresa)] }}
          />
          <span className="font-semibold text-[#1D1D1B]">
            {Capitalize(data?.empresa) || "---"}
          </span>
          · {month.toDate().toLocaleDateString("pt-BR", { month: "long", year: "numeric" })} · dia{" "}
          {elapsed} de {daysInMonth}
        </span>

        {/* KPIs */}
        <section aria-label="Indicadores do mês" className="flex flex-wrap gap-4">
          <div className="flex flex-[2_1_460px] flex-col gap-4 rounded-2xl bg-[#1D1D1B] p-5 text-white">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[13px] font-medium text-[#C9C8C2]">
                Tendência do mês
              </span>
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                  acima ? "bg-[#1E3A26] text-[#8FE3A5]" : "bg-[#3A1F14] text-[#F4A988]"
                }`}
              >
                {acima ? "acima da meta" : "abaixo da meta"}
              </span>
            </div>
            <span className="text-[44px] font-bold leading-none tabular-nums tracking-tight">
              {pctLabel(data?.tendencia || 0)}
            </span>
            <div className="flex flex-col gap-2.5">
              <div className="relative h-3 overflow-hidden rounded-full bg-[#3C3C3B]">
                <span
                  className="absolute inset-y-0 left-0 bg-[#7A3A22]"
                  style={{ width: `${Math.min(100, pct(data?.projecao, meta))}%` }}
                />
                <span
                  className="absolute inset-y-0 left-0 rounded-full bg-[#E84E1B]"
                  style={{ width: `${Math.min(100, pct(data?.producaoLiquido, meta))}%` }}
                />
              </div>
              <div className="flex flex-wrap justify-between gap-3 text-[13px] text-[#C9C8C2]">
                {[
                  { label: "Líquido", value: data?.producaoLiquido, color: "#E84E1B" },
                  { label: "Projeção", value: data?.projecao, color: "#7A3A22" },
                  { label: "Meta", value: meta },
                ].map((i) => (
                  <span key={i.label} className="inline-flex items-center gap-1.5">
                    {i.color && (
                      <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: i.color }} />
                    )}
                    {i.label}
                    <b className="font-semibold tabular-nums text-white">{num(i.value)}</b>
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className={kpiCard}>
            <span className={kpiLabel}>Produção</span>
            <span className={kpiValue}>{num(data?.producao)}</span>
            <span className="text-[13px] text-[#6B6B66]">bruta, no mês</span>
          </div>
          <div className={kpiCard}>
            <span className={kpiLabel}>Revistorias</span>
            <span className={kpiValue}>{num(data?.revistorias)}</span>
            <span className="self-start rounded-full bg-[#FDEDE6] px-2 py-0.5 text-xs font-semibold text-[#B83A12]">
              {pctLabel(pct(data?.revistorias, data?.producao))} da produção
            </span>
          </div>
          <div className={kpiCard}>
            <span className={kpiLabel}>Necessidade/dia</span>
            <span className={kpiValue}>{num(data?.necessidadeDia)}</span>
            <span className="text-[13px] text-[#6B6B66]">
              nos {restante} dias restantes
            </span>
          </div>
        </section>

        {/* Gráficos */}
        <section aria-label="Tendências" className="flex flex-wrap gap-4">
          <TrendChart
            title="Tendência geral"
            subtitle="Vistorias por dia vs. ritmo necessário"
            chartData={tendenciasGeral}
            reference={data?.necessidadeDia}
          />
          <TrendChart
            title="Por tipo de serviço"
            subtitle="Primeiro emplacamento vs. transferência"
            chartData={tendenciasTipoServico}
          />
          <TrendChart
            title="Por tipo de atendimento"
            subtitle="Loja vs. delivery"
            chartData={tendenciasAtendimento}
          />
          <TrendChart
            title="Por unidade"
            subtitle="Vistorias por dia em cada unidade"
            chartData={tendenciasUnidade}
          />
        </section>

        {/* Heatmap */}
        <section aria-label="Heatmap do mês" className={`${card} flex flex-col gap-[18px]`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-semibold">Heatmap do mês</h2>
              <span className="text-[13px] text-[#6B6B66]">Produção diária</span>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-[#6B6B66]">
              <span className="inline-flex items-center gap-1.5">
                Menos
                <span className="inline-flex gap-0.5">
                  {HEAT_COLORS.map((c) => (
                    <span key={c} className="h-3 w-4 rounded-[3px]" style={{ background: c }} />
                  ))}
                </span>
                Mais
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-3 w-4 rounded-[3px] bg-[#F1F0EC]" />
                Sem expediente
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="h-3 w-4 rounded-[3px] border border-dashed border-[#BDBCB6]" />
                A realizar
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-6">
            <div className="min-w-0 flex-[3_1_560px]">
              <ContinuousCalendar data={diaria} />
            </div>
            <div className="flex flex-[1_1_220px] flex-col gap-3">
              <div className={statBox}>
                <span className={kpiLabel}>Melhor dia</span>
                <span className="text-2xl font-bold tabular-nums">{num(best?.qtdTotal)}</span>
                <span className="text-[13px] text-[#6B6B66]">
                  {best ? dayjs(dayKey(best.data)).format("DD/MM") : "---"}
                </span>
              </div>
              <div className={statBox}>
                <span className={kpiLabel}>Média por dia trabalhado</span>
                <span className="text-2xl font-bold tabular-nums">{num(Math.round(media))}</span>
                <span className="text-[13px] text-[#6B6B66]">
                  vs. {num(data?.necessidadeDia)} necessários
                </span>
              </div>
              <div className={statBox}>
                <span className={kpiLabel}>Dias restantes</span>
                <span className="text-2xl font-bold tabular-nums">{restante}</span>
                <span className="text-[13px] text-[#6B6B66]">até o fim do mês</span>
              </div>
            </div>
          </div>
        </section>

        {/* Desempenho por unidade */}
        <section className="flex flex-col overflow-hidden rounded-2xl border border-[#E7E6E2] bg-white">
          <div className="flex flex-wrap items-baseline gap-2.5 px-5 py-4">
            <h2 className="text-base font-semibold">Desempenho em relação à meta</h2>
            <span className="text-[13px] text-[#6B6B66]">{unidades.length} unidades</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] border-collapse text-sm tabular-nums [&_td]:whitespace-nowrap [&_td]:px-3.5 [&_td]:py-3 [&_th]:whitespace-nowrap [&_th]:px-3.5 [&_th]:py-3">
              <thead>
                <tr className="bg-[#F7F7F5] text-right text-xs uppercase tracking-wider text-[#6B6B66] [&_th]:font-semibold">
                  <th className="text-left">Unidade</th>
                  <th>Meta</th>
                  <th>Realizado</th>
                  <th className="text-left">Atingido</th>
                  <th>Média/dia</th>
                  <th>Necessidade/dia</th>
                  <th>Projeção</th>
                  <th className="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {unidades.map((u, i) => {
                  const atingido = pct(u.qtdTotal, u.meta);
                  // Assume qtdMediaProjecao como projeção total do mês (mesma escala da meta)
                  const noRitmo = (u.qtdMediaProjecao || 0) >= (u.meta || 0);
                  return (
                    <tr
                      key={`${u.cidade}-${u.uf}`}
                      className="border-t border-[#EEEDE9] text-right hover:bg-[#FAFAF8]"
                    >
                      <td className="text-left">
                        <span className="inline-flex items-center gap-2.5 font-semibold">
                          <span
                            className="h-6 w-1 rounded-sm"
                            style={{ background: tendenciasUnidade?.series?.[i]?.color }}
                          />
                          {`${u.cidade} - ${u.uf}`}
                        </span>
                      </td>
                      <td>{num(u.meta)}</td>
                      <td className="font-bold">{num(u.qtdTotal)}</td>
                      <td className="text-left">
                        <div className="flex items-center gap-2.5">
                          <div className="relative h-2 w-[120px] rounded-full bg-[#F1F0EC]">
                            <span
                              className="absolute inset-y-0 left-0 rounded-full bg-[#E84E1B]"
                              style={{ width: `${Math.min(100, atingido)}%` }}
                            />
                            <span
                              className="absolute -top-[3px] h-3.5 w-0.5 bg-[#1D1D1B]"
                              style={{ left: `${pace}%` }}
                            />
                          </div>
                          <span className="w-12 text-[#4A4A46]">{pctLabel(atingido)}</span>
                        </div>
                      </td>
                      <td>{num(u.qtdMedia)}</td>
                      <td>{num(u.qtdMediaNecessaria)}</td>
                      <td>{num(u.qtdMediaProjecao)}</td>
                      <td className="text-left">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            noRitmo ? "bg-[#E6F4EA] text-[#1E6B33]" : "bg-[#FDEDE6] text-[#B83A12]"
                          }`}
                        >
                          {noRitmo ? "No ritmo" : "Abaixo do ritmo"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-[#1D1D1B] bg-[#F7F7F5] text-right font-bold">
                  <td className="text-left">Total</td>
                  <td>{num(sum("meta"))}</td>
                  <td>{num(sum("qtdTotal"))}</td>
                  <td className="text-left">{pctLabel(pct(sum("qtdTotal"), sum("meta")))}</td>
                  <td>{num(sum("qtdMedia"))}</td>
                  <td>{num(sum("qtdMediaNecessaria"))}</td>
                  <td>{num(sum("qtdMediaProjecao"))}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="flex flex-wrap gap-4 border-t border-[#EEEDE9] px-5 py-3 text-xs text-[#6B6B66]">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2.5 rounded-sm bg-[#E84E1B]" />
              Realizado / meta
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-0.5 bg-[#1D1D1B]" />
              Ritmo esperado hoje ({pctLabel(pace)} do mês)
            </span>
          </div>
        </section>
      </div>
    </LayoutTemplate>
  );
};
